import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { projectDataClient } from '../../../../lib/connect/clients';
import { useReextractContract } from './useReextractContract';
function isModificationFile(f) {
    const hay = [
        f.sourceKind,
        f.documentType,
        f.classificationLabel
    ].filter(Boolean).join(' ').toUpperCase();
    return hay.includes('MOD') || hay.includes('CONTRACT_MOD');
}
/**
 * Re-runs contract extraction stages 4→7 against stored MinIO artifacts.
 * Visible to callers with program edit access (delivery_pm update / delivery_vp).
 */ export function ReextractPanel() {
    const { programId, program, canEdit } = useProgramContext();
    const { reextract, running } = useReextractContract();
    const zouProjectId = program?.zouProjectId ?? '';
    const { data: projectFiles } = useQuery({
        queryKey: [
            'projectFiles',
            zouProjectId
        ],
        queryFn: ()=>projectDataClient.listProjectFiles({
                projectId: zouProjectId
            }),
        enabled: !!zouProjectId && canEdit,
        staleTime: 30000
    });
    const reextractDocId = useMemo(()=>{
        const files = projectFiles?.files ?? [];
        const base = files.find((f)=>f.baltigoDocId && !isModificationFile(f));
        return base?.baltigoDocId ?? files.find((f)=>f.baltigoDocId)?.baltigoDocId ?? '';
    }, [
        projectFiles?.files
    ]);
    if (!canEdit || !zouProjectId) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium text-foreground",
                        children: "Re-extract contract data"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "Re-runs base contract analysis against stored document artifacts (skips PDF conversion). Use to retest extraction changes."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraButton, {
                type: "button",
                variant: "secondary",
                size: "sm",
                disabled: running || !reextractDocId,
                onClick: ()=>void reextract({
                        zouProjectId,
                        programId,
                        reextractDocId,
                        documentKind: 'base',
                        label: 'Re-extracting contract',
                        link: `/delivery/programs/${programId}/docs`
                    }),
                "data-testid": "delivery-operational-reextract-button",
                children: [
                    /*#__PURE__*/ _jsx(RefreshCw, {
                        className: `mr-2 h-4 w-4 ${running ? 'animate-spin' : ''}`
                    }),
                    running ? 'Starting…' : 'Re-extract'
                ]
            })
        ]
    });
}
